/**
 * Bills Review Interstitial Modal
 * 
 * Shown before weekly closure completion when there are pending bills.
 * Calm, non-blocking design following ARC Doctrine.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { FileText, Calendar, AlertCircle } from "lucide-react";
import { format } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function BillsReviewInterstitial({ 
  isOpen, 
  onClose, 
  onReviewBills, 
  onContinueWithoutReview,
  pendingBills,
  householdId 
}) {
  // Safe destructure with defaults
  const draftBills = pendingBills?.draftBills || [];
  const needsReviewImports = pendingBills?.needsReviewImports || [];
  const count = pendingBills?.count || 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NZ', { 
      style: 'currency', 
      currency: 'NZD' 
    }).format(amount || 0);
  };

  const trackMetric = async (metricName) => {
    try {
      await fetch(`${API_URL}/api/beta/metrics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          metric_name: metricName,
          value: count,
          metadata: { household_id: householdId }
        })
      });
    } catch (error) {
      console.error('Failed to track metric:', error);
    }
  };

  const handleReviewBills = async () => {
    await trackMetric('weekly_close_review_clicked');
    onReviewBills();
  };

  const handleContinueWithoutReview = async () => {
    await trackMetric('weekly_close_continue_without_review_clicked');
    onContinueWithoutReview();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-md bg-white border-[#1a4d3e]/10"
        data-testid="bills-review-interstitial"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#1a4d3e] text-center">
            Bills to review
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {/* Calm message */}
          <p className="text-[#1a4d3e]/70 text-center mb-6">
            {count} new bill{count !== 1 ? 's were' : ' was'} added this week.
            <br />
            <span className="text-[#1a4d3e]/50">
              Reviewing them now helps you start the week with clarity.
            </span>
          </p>

          {/* Bills list */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {/* Draft bills */}
            {draftBills.map((bill) => (
              <div 
                key={bill.bill_id} 
                className="flex items-center gap-3 p-3 rounded-lg bg-[#faf9f6] border border-[#1a4d3e]/5"
                data-testid={`pending-bill-${bill.bill_id}`}
              >
                <div className="w-8 h-8 rounded-lg bg-[#1a4d3e]/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-[#1a4d3e]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1a4d3e] text-sm truncate">
                    {bill.name || bill.title || 'Unnamed bill'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-[#1a4d3e]/50">
                    {bill.due_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {format(new Date(bill.due_date), 'MMM d')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-medium text-[#1a4d3e] text-sm">
                    {formatCurrency(bill.amount_due || bill.amount)}
                  </p>
                  <Badge 
                    variant="outline" 
                    className="text-xs border-amber-200 text-amber-600 bg-amber-50"
                  >
                    Draft
                  </Badge>
                </div>
              </div>
            ))}

            {/* Needs review imports */}
            {needsReviewImports.map((item) => (
              <div 
                key={item.import_id || item.message_id} 
                className="flex items-center gap-3 p-3 rounded-lg bg-[#faf9f6] border border-[#1a4d3e]/5"
                data-testid={`pending-import-${item.import_id || item.message_id}`}
              >
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1a4d3e] text-sm truncate">
                    {item.extracted_data?.biller_name || item.subject || 'Email import'}
                  </p>
                  <p className="text-xs text-[#1a4d3e]/50 truncate">
                    {item.from_email || 'Unknown sender'}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  {item.extracted_data?.amount_due && (
                    <p className="font-medium text-[#1a4d3e] text-sm">
                      {formatCurrency(item.extracted_data.amount_due)}
                    </p>
                  )}
                  <Badge 
                    variant="outline" 
                    className="text-xs border-amber-200 text-amber-600 bg-amber-50"
                  >
                    Needs review
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 pt-2">
          <Button 
            className="w-full bg-[#1a4d3e] hover:bg-[#1a4d3e]/90 text-white"
            onClick={handleReviewBills}
            data-testid="review-bills-btn"
          >
            Review Bills
          </Button>
          <Button 
            variant="ghost" 
            className="w-full text-[#1a4d3e]/60 hover:text-[#1a4d3e] hover:bg-[#1a4d3e]/5"
            onClick={handleContinueWithoutReview}
            data-testid="continue-without-review-btn"
          >
            Continue Without Review
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default BillsReviewInterstitial;
